import React, { useState, useEffect } from 'react';
import { getAllBills, createBill, updateBill, deleteBill as deleteBillApi, getAllDoctors, getAllPatients, generateBillPDF } from '../../api';
import styles from './CreateBill.module.css';

const initialBillState = {
  patientName: '',
  phoneNumber: '',
  age: '',
  gender: '',
  bloodGroup: '',
  location: '',
  height: '',
  weight: '',
  bloodPressure: '',
  followUpDate: '',
  doctorName: '',
  doctorDesignation: '',
  dateTime: '',
  fees: '',
};

const CreateBill = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [form, setForm] = useState(initialBillState);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    fetchBills();
    fetchDoctorsAndPatients();
  }, []);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const data = await getAllBills();
      setBills(data);
    } catch (error) {
      showAlert('error', 'Error fetching bills');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctorsAndPatients = async () => {
    try {
      const [doctorData, patientData] = await Promise.all([
        getAllDoctors(),
        getAllPatients(),
      ]);
      setDoctors(Array.isArray(doctorData) ? doctorData : doctorData.doctors || []);
      setPatients(Array.isArray(patientData) ? patientData : patientData.data || []);
    } catch (error) {
      showAlert('error', 'Error fetching doctors or patients');
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
  };

  const resetForm = () => {
    setForm(initialBillState);
    setSelectedBill(null);
  };

  const handleAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEdit = (bill) => {
    setForm({ ...bill, dateTime: bill.dateTime ? bill.dateTime.slice(0, 16) : '' });
    setSelectedBill(bill);
    setShowEditModal(true);
  };

  const handleView = (bill) => {
    setSelectedBill(bill);
    setShowViewModal(true);
  };

  const handleDelete = (bill) => {
    setSelectedBill(bill);
    setShowDeleteModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'doctorName') {
      // Set fees automatically when doctor is selected
      const selectedDoc = doctors.find(doc => doc.name === value);
      setForm((prev) => ({
        ...prev,
        doctorName: value,
        doctorDesignation: selectedDoc ? selectedDoc.designationDegree : '',
        fees: selectedDoc ? selectedDoc.fees : '',
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const filteredBills = bills
    .filter(bill =>
      bill.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.doctorName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!form.patientName || !form.doctorName || !form.dateTime || !form.fees || !form.phoneNumber || !form.age || !form.gender) {
      showAlert('error', 'All marked fields are required');
      return;
    }
    try {
      await createBill({ ...form, fees: Number(form.fees) });
      setShowAddModal(false);
      showAlert('success', 'Bill added successfully!');
      resetForm();
      fetchBills();
    } catch (error) {
      showAlert('error', 'Error adding bill');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!form.patientName || !form.doctorName || !form.dateTime || !form.fees || !form.phoneNumber || !form.age || !form.gender) {
      showAlert('error', 'All marked fields are required');
      return;
    }
    try {
      await updateBill(selectedBill._id, { ...form, fees: Number(form.fees) });
      setShowEditModal(false);
      showAlert('success', 'Bill updated successfully!');
      resetForm();
      fetchBills();
    } catch (error) {
      showAlert('error', 'Error updating bill');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteBillApi(selectedBill._id);
      setShowDeleteModal(false);
      showAlert('success', 'Bill deleted successfully!');
      resetForm();
      fetchBills();
    } catch (error) {
      showAlert('error', 'Error deleting bill');
    }
  };

  const handleDownloadPDF = async (bill) => {
    try {
      showAlert('info', 'Generating PDF...');
      // Pass ?summary=today or ?summary=month as needed. Default includes both.
      await generateBillPDF(bill._id);
      showAlert('success', 'PDF downloaded successfully!');
    } catch (error) {
      showAlert('error', 'Error generating PDF');
    }
  };

  const handlePrescription = () => {
    if (!form.patientName || !form.doctorName) {
      showAlert('error', 'Please enter patient and doctor details first');
      return;
    }

    const selectedDoc = doctors.find(doc => doc.name === form.doctorName);
    const doctorDegree = form.doctorDesignation || (selectedDoc ? selectedDoc.designationDegree : '');
    const doctorReg = selectedDoc ? `Reg: ${selectedDoc.registrationNumber}` : '';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Prescription - ${form.patientName}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
            @page {
              size: 210mm 297mm;
              margin: 0;
            }
            body { 
              font-family: 'Roboto', sans-serif; 
              padding: 0; 
              margin: 0; 
              color: #333; 
              background: #f0f0f0;
            }
            .container {
              width: 210mm;
              height: 297mm;
              padding: 10mm 15mm;
              margin: 0 auto;
              background: white;
              box-sizing: border-box;
              position: relative;
              display: flex;
              flex-direction: column;
              overflow: hidden;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 2px solid #0033cc;
              padding-bottom: 5px;
              margin-bottom: 8px;
            }
            .header-left {
              width: 100px;
            }
            .header-center {
              flex: 1;
              text-align: center;
            }
            .header-right {
              width: 100px;
              text-align: right;
            }
            .clinic-name {
              font-size: 28px;
              font-weight: 700;
              color: #0033cc;
              margin: 0;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .tagline {
              font-size: 14px;
              font-style: normal;
              margin: 4px 0 0;
              color: #555;
            }
            .sub-tagline {
              font-size: 13px;
              font-weight: 600;
              margin: 4px 0 0;
            }
            .address {
              font-size: 12px;
              margin: 5px 0 0;
              color: #444;
            }
            .doctor-info {
              margin-bottom: 8px;
              border-bottom: 1px solid #eee;
              padding-bottom: 4px;
            }
            .doctor-name {
              font-size: 18px;
              font-weight: 700;
              color: #000;
            }
            .doctor-details {
              font-size: 13px;
              color: #555;
              margin-top: 4px;
            }
            .patient-box {
              border: 1px solid #000;
              padding: 10px 15px;
              margin-bottom: 20px;
              font-size: 13px;
            }
            .patient-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
            }
            .patient-field {
              flex: 1;
            }
            .patient-field strong {
              font-weight: 600;
              margin-right: 5px;
            }
            .rx-section {
              flex: 1;
              border: 1px solid #000;
              padding: 8px;
              position: relative;
              min-height: 0;
            }
            .rx-header {
              display: flex;
              justify-content: space-between;
              margin-bottom: 10px;
              border-bottom: 1px solid #eee;
              padding-bottom: 5px;
            }
            .rx-title {
              font-weight: 700;
              font-size: 16px;
              font-style: italic;
            }
            .footer {
              margin-top: 8px;
              padding-top: 5px;
            }
            .footer-top {
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              margin-bottom: 5px;
            }
            .follow-up {
              font-size: 12px;
              font-weight: 600;
              display: flex;
              align-items: center;
              gap: 5px;
            }
            .signature {
              text-align: right;
              font-size: 11px;
              border-top: 1px solid #333;
              padding-top: 5px;
              width: 140px;
            }
            .website-url {
              text-align: center;
              font-size: 11px;
              font-weight: 500;
              color: #0033cc;
              margin-bottom: 5px;
            }
            .disclaimer {
              font-size: 9px;
              text-align: center;
              color: #666;
            }
            .print-btn-container {
              position: fixed;
              bottom: 20px;
              right: 20px;
              z-index: 1000;
            }
            .print-btn {
              background: #0033cc;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 6px;
              cursor: pointer;
              font-size: 16px;
              box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            }
            .print-btn:hover { background: #002299; }
            @media print { 
              .print-btn-container { display: none; } 
              body { background: white; }
              .container { width: 100%; height: 100%; border: none; margin: 0; padding: 5mm 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="print-btn-container">
            <button class="print-btn" onclick="window.print()">Print</button>
          </div>
          <div class="container">
            <!-- Header -->
            <div class="header">
              <div class="header-left">
                <!-- Main Clinic Logo -->
                <img src="/assets/logo.jpg" alt="Clinic Logo" style="width: 90px; height: auto; object-fit: contain;">
              </div>
              <div class="header-center">
                <h1 class="clinic-name">Rupsa Clinic</h1>
                <p class="tagline">A Promise to Cure you Better</p>
                <p class="sub-tagline">We do Pathological Testing</p>
                <p class="address">Rupsa Clinic, Khadims Abasan Bidyakul Shopping Complex, Phone: +91 91239 33387</p>
              </div>
              <div class="header-right">
                <!-- Doctor symbol removed as requested -->
              </div>
            </div>

            <!-- Doctor Info -->
            <div class="doctor-info">
              <div class="doctor-name">Doctor Name: Dr. ${form.doctorName || '...................................'}</div>
              <div class="doctor-details">${doctorDegree} ${doctorReg ? `| ${doctorReg}` : ''}</div>
            </div>

            <!-- Patient Info Box -->
            <div class="patient-box">
              <div class="patient-row" style="border-bottom: 1px solid #eee; padding-bottom: 5px; margin-bottom: 8px;">
                <div class="patient-field"><strong>Patient Name :</strong> ${form.patientName}</div>
              </div>
              <div class="patient-row">
                <div class="patient-field"><strong>Age :</strong> ${form.age || ''}</div>
                <div class="patient-field" style="text-align: right;"><strong>Gender :</strong> ${form.gender || ''}</div>
              </div>
              <div class="patient-row">
                <div class="patient-field"><strong>Contact info :</strong> ${form.phoneNumber || ''}</div>
                <div class="patient-field" style="text-align: right;"><strong>Height :</strong> ${form.height || 'N/A'}</div>
              </div>
              <div class="patient-row">
                <div class="patient-field"><strong>Location :</strong> ${form.location || ''}</div>
                <div class="patient-field" style="text-align: right;"><strong>Weight :</strong> ${form.weight || 'N/A'}</div>
              </div>
              <div class="patient-row">
                <div class="patient-field"></div>
                <div class="patient-field" style="text-align: right;"><strong>Blood Pressure :</strong> ${form.bloodPressure || 'N/A'}</div>
              </div>
            </div>

            <!-- Prescription Body -->
            <div class="rx-section">
              <div class="rx-header">
                <div class="rx-title">Prescription & Instructions</div>
                <div class="rx-title">Date : ${new Date().toLocaleDateString()}</div>
              </div>
              <!-- Content Area for handwriting or typed prescription -->
            </div>

            <!-- Footer -->
            <div class="footer">
              <div class="website-url">www.rupsaclinic.com</div>
              <div class="footer-top">
                <div class="follow-up">
                  Follow Up <span style="color: blue;">&#9658;</span> Date : ${form.followUpDate ? new Date(form.followUpDate).toLocaleDateString() : '.........................'}
                </div>
                <div class="signature">
                  Doctor's Signature
                </div>
              </div>
              <div class="disclaimer">
                Kindly Note: Incase of any emergency please visit nearest State Govt. Hospital
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading bills...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Billing Management</h2>
        <p>Create, edit, delete, and view billing information</p>
      </div>

      {alert.show && (
        <div className={`${styles.alert} ${styles[`alert${alert.type.charAt(0).toUpperCase() + alert.type.slice(1)}`]}`}>
          {alert.message}
        </div>
      )}

      <div className={styles.actions}>
        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={handleAdd}
        >
          <span>+</span> Add New Bill
        </button>
      </div>

      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search bills by patient or doctor name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tableContainer}>
        {filteredBills.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No bills found</h3>
            <p>{searchTerm ? 'Try adjusting your search terms' : 'Start by adding a new bill'}</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th>Patient Name</th>
                <th>Visited Doctor</th>
                <th>Date & Time</th>
                <th>Fees</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className={styles.tableBody}>
              {filteredBills.map((bill) => (
                <tr key={bill._id}>
                  <td>{bill.patientName}</td>
                  <td>{bill.doctorName}</td>
                  <td>{new Date(bill.dateTime).toLocaleString()}</td>
                  <td>Rs. {Number(bill.fees || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className={styles.tableActions}>
                    <button
                      className={`${styles.btn} ${styles.btnSmall} ${styles.btnSecondary}`}
                      onClick={() => handleView(bill)}
                    >
                      View
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnSmall} ${styles.btnPrimary}`}
                      onClick={() => handleEdit(bill)}
                    >
                      Edit
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnSmall} ${styles.btnSuccess}`}
                      onClick={() => handleDownloadPDF(bill)}
                    >
                      PDF
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnSmall} ${styles.btnDanger}`}
                      onClick={() => handleDelete(bill)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Add New Bill</h3>
              <button
                className={styles.closeBtn}
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAddSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Name of Patient *</label>
                <input
                  type="text"
                  name="patientName"
                  value={form.patientName}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter patient name"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Phone Number *</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter phone number"
                  required
                  pattern="[0-9]{10}"
                  title="Please enter a valid 10-digit phone number"
                />
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Age *</label>
                  <input
                    type="number"
                    name="age"
                    value={form.age}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="Age"
                    required
                    min="0"
                    max="120"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Gender *</label>
                  <select
                    name="gender"
                    value={form.gender}
                    onChange={handleFormChange}
                    className={styles.input}
                    required
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Blood Group</label>
                <select
                  name="bloodGroup"
                  value={form.bloodGroup}
                  onChange={handleFormChange}
                  className={styles.input}
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Height</label>
                  <input
                    type="text"
                    name="height"
                    value={form.height}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="e.g. 5'8&quot;"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Weight</label>
                  <input
                    type="text"
                    name="weight"
                    value={form.weight}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="e.g. 70kg"
                  />
                </div>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Blood Pressure</label>
                  <input
                    type="text"
                    name="bloodPressure"
                    value={form.bloodPressure}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="e.g. 120/80"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Location</label>
                  <input
                    type="text"
                    name="location"
                    value={form.location}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="Patient Location"
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Follow Up Date</label>
                <input
                  type="date"
                  name="followUpDate"
                  value={form.followUpDate ? form.followUpDate.slice(0, 10) : ''}
                  onChange={handleFormChange}
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Visited Doctor *</label>
                <select
                  name="doctorName"
                  value={form.doctorName}
                  onChange={handleFormChange}
                  className={styles.input}
                  required
                >
                  <option value="">Select doctor</option>
                  {doctors.map((d) => (
                    <option key={d._id} value={d.name}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Doctor Designation & Degree</label>
                <input
                  type="text"
                  name="doctorDesignation"
                  value={form.doctorDesignation}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Designation and degree will appear here"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Date & Time *</label>
                <input
                  type="datetime-local"
                  name="dateTime"
                  value={form.dateTime}
                  onChange={handleFormChange}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Fees *</label>
                <input
                  type="number"
                  name="fees"
                  value={form.fees}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter fees"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handlePrescription}
                >
                  Add Prescription
                </button>
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnSuccess}`}
                  onClick={handleAddSubmit} // explicitly call submit if button type is submit it's fine, but just ensuring
                >
                  Add Bill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Edit Bill</h3>
              <button
                className={styles.closeBtn}
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Name of Patient *</label>
                <input
                  type="text"
                  name="patientName"
                  value={form.patientName}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter patient name"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Phone Number *</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter phone number"
                  required
                  pattern="[0-9]{10}"
                  title="Please enter a valid 10-digit phone number"
                />
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Age *</label>
                  <input
                    type="number"
                    name="age"
                    value={form.age}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="Age"
                    required
                    min="0"
                    max="120"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Gender *</label>
                  <select
                    name="gender"
                    value={form.gender}
                    onChange={handleFormChange}
                    className={styles.input}
                    required
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Blood Group</label>
                <select
                  name="bloodGroup"
                  value={form.bloodGroup}
                  onChange={handleFormChange}
                  className={styles.input}
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Height</label>
                  <input
                    type="text"
                    name="height"
                    value={form.height}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="e.g. 5'8&quot;"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Weight</label>
                  <input
                    type="text"
                    name="weight"
                    value={form.weight}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="e.g. 70kg"
                  />
                </div>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Blood Pressure</label>
                  <input
                    type="text"
                    name="bloodPressure"
                    value={form.bloodPressure}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="e.g. 120/80"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Location</label>
                  <input
                    type="text"
                    name="location"
                    value={form.location}
                    onChange={handleFormChange}
                    className={styles.input}
                    placeholder="Patient Location"
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Follow Up Date</label>
                <input
                  type="date"
                  name="followUpDate"
                  value={form.followUpDate ? form.followUpDate.slice(0, 10) : ''}
                  onChange={handleFormChange}
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Visited Doctor *</label>
                <select
                  name="doctorName"
                  value={form.doctorName}
                  onChange={handleFormChange}
                  className={styles.input}
                  required
                >
                  <option value="">Select doctor</option>
                  {doctors.map((d) => (
                    <option key={d._id} value={d.name}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Doctor Designation & Degree</label>
                <input
                  type="text"
                  name="doctorDesignation"
                  value={form.doctorDesignation}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Designation and degree will appear here"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Date & Time *</label>
                <input
                  type="datetime-local"
                  name="dateTime"
                  value={form.dateTime}
                  onChange={handleFormChange}
                  className={styles.input}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Fees *</label>
                <input
                  type="number"
                  name="fees"
                  value={form.fees}
                  onChange={handleFormChange}
                  className={styles.input}
                  placeholder="Enter fees"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className={styles.formActions}>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handlePrescription}
                >
                  Add Prescription
                </button>
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnSuccess}`}
                >
                  Update Bill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedBill && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Bill Details</h3>
              <button
                className={styles.closeBtn}
                onClick={() => setShowViewModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.viewDetails}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Patient Name</span>
                <span className={styles.detailValue}>{selectedBill.patientName}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Phone</span>
                <span className={styles.detailValue}>{selectedBill.phoneNumber || 'N/A'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Age / Gender</span>
                <span className={styles.detailValue}>{selectedBill.age || '-'} / {selectedBill.gender || '-'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Blood Group</span>
                <span className={styles.detailValue}>{selectedBill.bloodGroup || 'N/A'}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Visited Doctor</span>
                <span className={styles.detailValue}>{selectedBill.doctorName}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Date & Time</span>
                <span className={styles.detailValue}>{new Date(selectedBill.dateTime).toLocaleString()}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Fees</span>
                <span className={styles.detailValue}>Rs. {selectedBill.fees}</span>
              </div>
            </div>
            <div className={styles.formActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSuccess}`}
                onClick={() => handleDownloadPDF(selectedBill)}
              >
                Download PDF
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedBill && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Confirm Delete</h3>
              <button
                className={styles.closeBtn}
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Are you sure you want to delete the bill for <b>{selectedBill.patientName}</b>?</p>
            </div>
            <div className={styles.formActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnDanger}`}
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateBill; 